/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './AddressValidationModal.scss';

import { isEmpty } from 'lodash';
import { func, object, string } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import BaseModal from 'components/shared/Modal/BaseModal';
import ModalLoadingSpinner from 'components/shared/Modal/ModalLoadingSpinner';
import {
  validateAddresses
} from 'services/Rest';
import {
  RESIDENTIAL_ADDRESS,
  MAILING_ADDRESS
} from 'services/Preferences';
import {
  getCustomerName,
} from 'state/customer/CustomerSelectors';

import AddressFound from './AddressFound';
import NoAddressFound from './NoAddressFound';
import {
  getStandardizedAddress,
  buildAddressesToValidate,
  getRequiredAddressValidationFieldsEmpty
} from 'services/AddressValidationUtils';

class AddressValidationModal extends React.Component {
  state = {
    addressFound: false,
    selectedAddress: null,
    mailingAddressFound: false,
    mailingAddressDifferent: false,
    residentialAddressDifferent: false,
    validatedAddresses: {},
    isLoading: true
  };

  componentDidMount() {
    const {
      enteredAddress,
      updatedContactInformation,
      hasResidential
    } = this.props;

    if(hasResidential) {
      const {
        primaryMailingAddress,
        residentialAddress
      } = updatedContactInformation;
      const { sameAsResidential = false } = primaryMailingAddress;
      const addressesToValidate = buildAddressesToValidate(
        getStandardizedAddress(primaryMailingAddress),
        getStandardizedAddress(residentialAddress),
        sameAsResidential
      );

      if(addressesToValidate.length) {
        validateAddresses(addressesToValidate, sameAsResidential)
          .then((response) => {
            let addresses;

            const residentialResponse = response.find(addr => addr.typeId === RESIDENTIAL_ADDRESS);
            const mailingResponse = response.find(addr => addr.typeId === MAILING_ADDRESS);

            if(sameAsResidential) {
              //Only send in one address so set both to first index in response.
              addresses = {
                validatedResidentialAddress: residentialResponse && residentialResponse,
                validatedMailingAddress: residentialResponse && residentialResponse
              };
            } else {
              //send in both address, return of promise is returned in order sent in
              addresses = {
                validatedResidentialAddress: residentialResponse && residentialResponse,
                validatedMailingAddress: mailingResponse && mailingResponse
              };
            }

            this.setState({validatedAddresses: addresses});
          })
          .catch(() =>{
            //do nothing
          });
      }
    } else {
      const { disposition, ...restOfEnteredAddress } = enteredAddress; // eslint-disable-line
      const addressesToValidate = [
        {...restOfEnteredAddress}
      ]
      validateAddresses(addressesToValidate)
        .then((response) => {
          const mailingResponse = response.find(addr => addr.typeId === MAILING_ADDRESS);
          const addresses = {
            validatedMailingAddress: mailingResponse
          };
          this.setState({validatedAddresses: addresses});
        })
        .catch(() => {
          //do nothing
        });
    }

  }

  componentDidUpdate(prevProps, prevState) {
    const { hasResidential } = this.props;
    const { validatedAddresses } = this.state;

    if(!isEmpty(validatedAddresses) && isEmpty(prevState.validatedAddresses)) {
      if(hasResidential) {
        this.checkResidentialAndMailingAgainstValidation();
      } else {
        this.checkEnteredAddressAgainstValidation();
      }
    }
  }

  checkResidentialAndMailingAgainstValidation = () => {
    const { updatedContactInformation, handleUpdatedContactInformationChange, onSelect } = this.props;
    const { validatedAddresses } = this.state;
    const { validatedMailingAddress, validatedResidentialAddress } = validatedAddresses;
    const { primaryMailingAddress, residentialAddress } = updatedContactInformation;
    const { sameAsResidential } = primaryMailingAddress;

    const noResidentialAddressFound = getRequiredAddressValidationFieldsEmpty(validatedResidentialAddress);
    const noMailingAddressFound = getRequiredAddressValidationFieldsEmpty(validatedMailingAddress);

    let isResidentialSameAsValidated;
    let isMailingSameAsValidated;


    if (noResidentialAddressFound) {
      this.setState({ addressFound: false, isLoading: false });
    } else {
      const addressKeys = ['city', 'state', 'zipCode', 'zipCodeExt'];
      const sameAddress = addressKeys.every(
        (key) => residentialAddress[key] === validatedResidentialAddress[key]
      );
      const sameAddressLines =
        residentialAddress.addressLines.join('') === validatedResidentialAddress.addressLines.join('');
      if (sameAddress && sameAddressLines) {
        this.setState({ addressFound: true });
        isResidentialSameAsValidated = true;
        handleUpdatedContactInformationChange(updatedContactInformation);
      } else {
        this.setState({ addressFound: true, residentialAddressDifferent: true, isLoading: false });
      }
    }

    if(noMailingAddressFound) {
      this.setState({ mailingAddressFound: false, isLoading: false });
    } else {
      const addressKeys = ['city', 'state', 'zipCode', 'zipCodeExt'];
      const sameAddress = addressKeys.every(
        (key) => primaryMailingAddress[key] === validatedMailingAddress[key]
      );

      const sameAddressLines =
        primaryMailingAddress.addressLines.join('') === validatedMailingAddress.addressLines.join('');
      if (sameAddress && sameAddressLines) {
        this.setState({ mailingAddressFound: true });
        isMailingSameAsValidated = true;
        handleUpdatedContactInformationChange(updatedContactInformation);
      } else {
        this.setState({ mailingAddressFound: true, mailingAddressDifferent: true, isLoading: false });
      }
    }

    if(
      (sameAsResidential && isResidentialSameAsValidated) ||
      (isMailingSameAsValidated && isResidentialSameAsValidated) ||
      (primaryMailingAddress.foreign && isResidentialSameAsValidated) ||
      (residentialAddress.foreign && isMailingSameAsValidated)
    ) {
      onSelect(updatedContactInformation);
    }
  }

  checkEnteredAddressAgainstValidation = () => {
    const { enteredAddress, updatedContactInformation } = this.props;
    const { validatedAddresses } = this.state;
    const { validatedMailingAddress } = validatedAddresses;
    const noAddressFound = Object.keys(validatedMailingAddress).every((key) =>
      isEmpty(validatedMailingAddress[key])
    );
    if (noAddressFound) {
      this.setState({ addressFound: false, isLoading: false });
    } else {
      const addressKeys = ['city', 'state', 'zipCode', 'zipCodeExt'];
      const sameAddress = addressKeys.every(
        (key) => enteredAddress[key] === validatedMailingAddress[key]
      );
      const sameAddressLines =
        enteredAddress.addressLines.join('') ===
        validatedMailingAddress.addressLines.join('');
      if (sameAddress && sameAddressLines) {
        const { onSelect } = this.props;
        onSelect(updatedContactInformation);
      } else {
        this.setState({ addressFound: true, isLoading: false });
      }
    }
  };

  handleAddressSelect = (address) => {
    const { onSelect } = this.props;
    onSelect(address);
  };

  handleModalClose = () => this.props.onClose();

  render() {
    const {
      enteredAddress,
      legalName,
      updatedContactInformation,
      hasResidential
    } = this.props;
    const {
      addressFound,
      mailingAddressFound,
      residentialAddressDifferent,
      mailingAddressDifferent,
      validatedAddresses,
      isLoading
    } = this.state;

    const { primaryMailingAddress: { sameAsResidential = false } } = updatedContactInformation;
    let validatedResidential;
    let validatedMailing;

    if(!isEmpty(validatedAddresses)) {
      if(hasResidential) {
        const { validatedMailingAddress, validatedResidentialAddress } = validatedAddresses;
        validatedMailing = validatedMailingAddress;
        validatedResidential = validatedResidentialAddress;
      } else {
        const { validatedMailingAddress } = validatedAddresses;
        validatedMailing = validatedMailingAddress;
      }
    }

    return (
      <BaseModal
        close={this.handleModalClose}
        containerClassName="address-validation-modal"
        disableClickOff={true}
        hideAnchor={true}
        hideClose={true}
        isOpen={true}
        title="U.S. Postal Address Verification">
        {isLoading && <ModalLoadingSpinner label="Validating address" />}
        {!isLoading &&
          (addressFound || mailingAddressFound
              ? <AddressFound
                enteredAddress={enteredAddress}
                name={legalName}
                addressFound={addressFound}
                mailingAddressFound={mailingAddressFound}
                onClose={this.handleModalClose}
                onSelect={this.handleAddressSelect}
                hasResidential={hasResidential}
                validatedAddress={hasResidential ? validatedResidential : validatedMailing}
                residentialAddressDifferent={residentialAddressDifferent}
                mailingAddressDifferent={mailingAddressDifferent}
                validatedMailingAddress={validatedMailing}
                isMailingSameAsResidential={sameAsResidential}
                updatedContactInformation={updatedContactInformation} />
              : <NoAddressFound
                enteredAddress={enteredAddress}
                name={legalName}
                hasResidential={hasResidential}
                onClose={this.handleModalClose}
                onSelect={this.handleAddressSelect}
                isMailingSameAsResidential={sameAsResidential}
                updatedContactInformation={updatedContactInformation} />
          )
        }
      </BaseModal>
    );
  }
}

AddressValidationModal.propTypes = {
  enteredAddress: object.isRequired,
  onClose: func.isRequired,
  legalName: string.isRequired,
  onSelect: func.isRequired,
};

const mapStateToProps = (state) => ({
  legalName: getCustomerName(state),
});

export default connect(mapStateToProps)(AddressValidationModal);
